import React, { useEffect, useRef, useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Button, Card, Col, Form, Modal, Row } from "react-bootstrap";

import {
  updateWorkPackageData,
  createWorkPackage,
  fetchWorkPackages,
} from "../../../../store/features/workPackage/workPackageSlice";

import { fetchResourceAllocations } from "../../../../store/features/resourceAllocation/resourceAllocationSlice";
import { fetchManagersByUser } from "../../../../store/features/user/userSlice";
import { fetchProjectById } from "../../../../store/features/project/projectSlice";
import { formatDate } from "../../../../utils/formatDateForInput";

const WorkPackageFormData = ({
  setShowWpModal,
  showWpModal,
  setWorkPackaesInfo,
  workPackagesInfo,
}) => {
  const dispatch = useDispatch();
  const { managersByUser = [] } = useSelector((state) => state.users);
  const { role } = useSelector((state) => state.auth);
  const { resourceAllocations } = useSelector(
    (state) => state.resourceAllocations,
  );
  const { workPackages } = useSelector((state) => state.workPackages);
  const { projects, projectById } = useSelector((state) => state.projects);

  const [managersOptions, setManagersOptions] = useState([]);
  const [projectId, setProjectId] = useState(projectById._id || "");
  const formikRef = useRef();

  // Dynamic validation schema using project start and end dates
  const getValidationSchema = (projectById) =>
    Yup.object().shape({
      title: Yup.string().required("Workpackage title is required"),
      description: Yup.string().required("Description is required"),
      wpNumber: Yup.string().required("Workpackage number is required"),
      status: Yup.string().required("Status is required"),
      projectId: Yup.string().required("Project name is required"),
      managerIds: Yup.string().required("Manager name is required"),
      startDate: Yup.date()
        .required("Start date is required")
        .when("projectId", (projectId, schema) => {
          if (projectId && projectById?.startDate && projectById?.endDate) {
            return schema
              .test(
                "is-within-project-start",
                `Start date must be on or after project start (${formatDate(
                  projectById.startDate.slice(0, 10),
                )})`,
                function (value) {
                  if (!value) return true;
                  const inputDate = new Date(value);
                  inputDate.setHours(0, 0, 0, 0);
                  const projectStart = new Date(projectById.startDate);
                  projectStart.setHours(0, 0, 0, 0);
                  return inputDate >= projectStart;
                },
              )
              .test(
                "is-within-project-end",
                `Start date must be on or before project end (${formatDate(
                  projectById.endDate.slice(0, 10),
                )})`,
                function (value) {
                  if (!value) return true;
                  const inputDate = new Date(value);
                  inputDate.setHours(0, 0, 0, 0);
                  const projectEnd = new Date(projectById.endDate);
                  projectEnd.setHours(0, 0, 0, 0);
                  return inputDate <= projectEnd;
                },
              );
          }
          return schema;
        }),

      endDate: Yup.date()
        .required("End date is required")
        .test(
          "is-same-or-after-start",
          "End date must be on or after start date",
          function (value) {
            const { startDate } = this.parent;
            if (!startDate || !value) return true;
            const inputEndDate = new Date(value);
            inputEndDate.setHours(0, 0, 0, 0);
            const inputStartDate = new Date(startDate);
            inputStartDate.setHours(0, 0, 0, 0);
            return inputEndDate >= inputStartDate;
          },
        )
        .when("projectId", (projectId, schema) => {
          if (projectId && projectById?.endDate) {
            return schema.test(
              "is-within-project-end",
              `End date must be on or before project end (${formatDate(
                projectById.endDate.slice(0, 10),
              )})`,
              function (value) {
                if (!value) return true;
                const inputDate = new Date(value);
                inputDate.setHours(0, 0, 0, 0);
                const projectEnd = new Date(projectById.endDate);
                projectEnd.setHours(0, 0, 0, 0);
                return inputDate <= projectEnd;
              },
            );
          }
          return schema;
        }),
    });

  const initialValues = {
    title: workPackagesInfo.title || "",
    description: workPackagesInfo.description || "",
    notes: workPackagesInfo.notes || "",
    addNotes: !!workPackagesInfo.notes,
    wpNumber: workPackagesInfo.wpNumber || "",
    managerIds: workPackagesInfo.managerIds
      ? workPackagesInfo.managerIds[0]
      : "",
    startDate: workPackagesInfo.startDate
      ? workPackagesInfo.startDate.slice(0, 10)
      : "",
    endDate: workPackagesInfo.endDate
      ? workPackagesInfo.endDate.slice(0, 10)
      : "",
    personMonths: workPackagesInfo.personMonths || "",
    assistantSuperAdminApproval: false,
    wpAttachments: workPackagesInfo.wpAttachments || [],
    status: workPackagesInfo.status || "",
    approvalStatus: workPackagesInfo.approvalStatus || "pending",
    rejectionReason: workPackagesInfo.rejectionReason || "",
    cost: workPackagesInfo.cost || "",
    projectId: projectById._id || "",
    role,
  };
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const projectWps = workPackages?.filter(
        (wp) =>
          wp.projectId === projectById._id && wp._id !== workPackagesInfo._id,
      );
      const totalCostWp = projectWps?.reduce(
        (acc, item) => acc + Number(item.cost),
        0,
      );
      const wpCost = totalCostWp + Number(values.cost);
      if (!isNaN(wpCost) && wpCost > projectById.budget) {
        toast.error(
          `Work package cost exceeds the project budget of ${projectById.budget}.`,
        );
        setSubmitting(false);
        return;
      }

      const formData = new FormData();
      for (let key in values) {
        if (key === "wpAttachments") {
          values.wpAttachments.forEach((file) => {
            if (file instanceof File) {
              formData.append("wpAttachments", file);
            } else {
              formData.append("wpAttachments", file);
            }
          });
        } else if (key !== "addNotes") {
          if (key === "notes" && !values.addNotes) {
            continue;
          }
          formData.append(key, values[key]);
        }
      }

      if (workPackagesInfo._id) {
        formData.append("_id", workPackagesInfo._id);
        await dispatch(updateWorkPackageData(formData)).unwrap();
        toast.success("WorkPackage updated!");
      } else {
        formData.append(
          "assistantSuperAdminId",
          projectById.assistantSuperAdminId,
        );
        await dispatch(createWorkPackage(formData)).unwrap();
        toast.success("WorkPackage created!");
      }

      await dispatch(fetchWorkPackages());
      setWorkPackaesInfo({});
      setShowWpModal(false);
      resetForm();
    } catch (err) {
      toast.error("Error saving WorkPackage");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (projectById.assistantSuperAdminId) {
      dispatch(fetchManagersByUser(projectById.assistantSuperAdminId));
      dispatch(fetchResourceAllocations());
    } else {
      setManagersOptions([]);
    }
  }, [projectById.assistantSuperAdminId, dispatch]);

  useEffect(() => {
    const allocation = resourceAllocations.find(
      (ra) => ra.assistantSuperadmin?._id === projectById.assistantSuperAdminId,
    );
    const allocManagers = allocation?.resource?.map((r) => r.manager) || [];
    const apiManagers = Array.isArray(managersByUser)
      ? managersByUser[0]?.createdUsers || []
      : [];
    const combinedManagers = [...allocManagers, ...apiManagers];
    const uniqueManagers = Array.from(
      new Map(combinedManagers.map((m) => [m?._id?.toString(), m])).values(),
    );
    setManagersOptions(uniqueManagers);
  }, [resourceAllocations, managersByUser, projectById.assistantSuperAdminId]);

  useEffect(() => {
    if (projectId || workPackagesInfo.projectId) {
      dispatch(fetchProjectById(workPackagesInfo.projectId || projectId));
    }
  }, [projectId, dispatch, workPackagesInfo.projectId]);

  const STATUS_OPTIONS = [
    "Not Started",
    "In Progress",
    "Submitted",
    "Completed",
    "Approve",
    "Rejected",
  ];

  return (
    <Modal
      size="lg"
      show={showWpModal}
      onHide={() => setShowWpModal(false)}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {workPackagesInfo._id ? "Update" : "Add"} Work Package
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Card>
          <Card.Body>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={getValidationSchema(projectById)}
              onSubmit={handleSubmit}
              innerRef={formikRef}
            >
              {({
                values,
                handleChange,
                handleSubmit,
                touched,
                errors,
                isSubmitting,
                resetForm,
                setFieldValue,
              }) => (
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Project Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          name="projectId"
                          value={values.projectId}
                          onChange={(e) => {
                            handleChange(e);
                            setProjectId(e.currentTarget.value);
                          }}
                          isInvalid={touched.projectId && !!errors.projectId}
                        >
                          <option value="">Select Project</option>
                          {projects?.map((project) => (
                            <option key={project._id} value={project._id}>
                              {project.projectName}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {errors.projectId}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Assign to Manager{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          name="managerIds"
                          value={values.managerIds}
                          onChange={handleChange}
                          isInvalid={touched.managerIds && !!errors.managerIds}
                        >
                          <option value="">Select Manager</option>
                          {managersOptions.map((m) => (
                            <option key={m._id} value={m._id}>
                              {m.name}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {errors.managerIds}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Wp Title <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          name="title"
                          value={values.title}
                          onChange={handleChange}
                          isInvalid={touched.title && !!errors.title}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.title}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md={8}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Description <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          name="description"
                          value={values.description}
                          onChange={handleChange}
                          isInvalid={
                            touched.description && !!errors.description
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.description}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Wp Number <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          name="wpNumber"
                          value={values.wpNumber}
                          onChange={handleChange}
                          isInvalid={touched.wpNumber && !!errors.wpNumber}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.wpNumber}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Cost</Form.Label>
                        <Form.Control
                          type="number"
                          name="cost"
                          value={values.cost}
                          onChange={handleChange}
                          isInvalid={touched.cost && !!errors.cost}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.cost}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Start Date</Form.Label>
                        <Form.Control
                          type="date"
                          name="startDate"
                          value={values.startDate}
                          onChange={handleChange}
                          isInvalid={touched.startDate && errors.startDate}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.startDate}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>End Date</Form.Label>
                        <Form.Control
                          type="date"
                          name="endDate"
                          value={values.endDate}
                          onChange={handleChange}
                          isInvalid={touched.endDate && !!errors.endDate}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.endDate}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Status <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          name="status"
                          value={values.status}
                          onChange={handleChange}
                          isInvalid={touched.status && !!errors.status}
                        >
                          <option value="">Select Status</option>
                          {STATUS_OPTIONS.map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {errors.status}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* Attachments */}
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Attachments</Form.Label>
                        <Form.Control
                          type="file"
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.currentTarget.files);
                            const existing = values.wpAttachments || [];
                            setFieldValue("wpAttachments", [
                              ...existing,
                              ...files,
                            ]);
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Check
                        type="checkbox"
                        label="Add Notes"
                        name="addNotes"
                        checked={values.addNotes}
                        onChange={(e) =>
                          setFieldValue("addNotes", e.target.checked)
                        }
                        className="mb-2"
                      />
                      {values.addNotes && (
                        <Form.Group className="mb-3">
                          <Form.Label>Notes</Form.Label>
                          <Form.Control
                            as="textarea"
                            name="notes"
                            value={values.notes}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      )}
                    </Col>
                  </Row>

                  <Row>
                    <Col className="text-end">
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setWorkPackaesInfo({});
                          resetForm();
                          setShowWpModal(false);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="ms-2"
                        variant="primary"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {workPackagesInfo._id ? "Update" : "Create"}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              )}
            </Formik>
          </Card.Body>
        </Card>
      </Modal.Body>
    </Modal>
  );
};

export default WorkPackageFormData;
